import { useEffect, useRef } from 'react';
import { z } from 'zod';
import { useDispatch } from 'react-redux';
import { Button, Stack, Link } from '@optimus-web/core';
import {
    createZodFormResolver,
    FormInputField,
    LocalizedText,
    NotAllowedError,
    Form,
    Validation,
} from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import {
    GeneralArea,
    GeneralDeviceSystem,
    GeneralSingleSignOnInfo,
    useRepositoryMutation,
} from '@mews-commander/data-access';
import { GENERAL_SIGN_IN_PARAMS, initializeUserSession } from '../../../../ducks/actions';
import { deleteLockedUser, useSelectedLockedUser } from '../useLockscreenStore';

const schema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('password'),
        password: Validation.String.Required,
    }),
    z.object({
        type: z.literal('pin'),
        pin: Validation.String.Required,
    }),
]);

export type unlockFormValues = z.infer<typeof schema>;
const resolver = createZodFormResolver(schema);

interface UnlockFormCredentialsProps {
    userId: string;
    hasUnlockPin: boolean;
    isSaving: boolean;
    ssoData: GeneralSingleSignOnInfo | undefined | null;
    setIsSaving: (isSaving: boolean) => void;
}

export function UnlockFormCredentials({
    ssoData,
    userId,
    hasUnlockPin,
    isSaving,
    setIsSaving,
}: UnlockFormCredentialsProps) {
    const dispatch = useDispatch();
    const lockscreenSelectedUser = useSelectedLockedUser();
    const { mutateAsync } = useRepositoryMutation('generalUsers', 'unlock');

    const onSubmit = async ({ type, ...passwordOrPin }: unlockFormValues) => {
        setIsSaving(true);

        try {
            await mutateAsync({
                ...GENERAL_SIGN_IN_PARAMS,
                ...passwordOrPin,
                // @ts-expect-error token is usually sent with every request, but here we need to explicitly send value from store
                accessToken: lockscreenSelectedUser?.accessToken,
                device: { system: GeneralDeviceSystem.Web, id: lockscreenSelectedUser?.deviceId },
                application: GeneralArea.Commander,
            }).then((data) => {
                if (data.accessInfo) {
                    void dispatch(initializeUserSession(data));
                    deleteLockedUser(userId);
                }
            });
        } catch (error) {
            setIsSaving(false);

            if (error instanceof NotAllowedError) {
                if (userId) {
                    deleteLockedUser(userId);
                }
            }

            throw error;
        }
    };

    return (
        <Form onSubmit={onSubmit} resolver={resolver} defaultValues={{ type: hasUnlockPin ? 'pin' : 'password' }}>
            <Stack vertical spacing="200">
                <AutofocusedField hasUnlockPin={hasUnlockPin} />
                <Button type="submit" variant="primary" wide disabled={isSaving}>
                    <LocalizedText textKey={TextKey.Unlock} />
                </Button>
                {ssoData?.signInUrl && (
                    <Stack alignment="center">
                        <Link href={ssoData?.signInUrl} inherit>
                            <LocalizedText textKey={TextKey.UnlockWithSso} />
                        </Link>
                    </Stack>
                )}
            </Stack>
        </Form>
    );
}

const passwordInputProps = {
    label: <LocalizedText textKey={TextKey.Password} />,
    name: 'password',
    id: 'password',
};

const pinInputProps = {
    label: <LocalizedText textKey={TextKey.PinCode} />,
    name: 'pin',
    id: 'pin',
    'data-lpignore': true,
};

const AutofocusedField = ({ hasUnlockPin }: { hasUnlockPin: boolean }) => {
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    return (
        <FormInputField
            {...(hasUnlockPin ? pinInputProps : passwordInputProps)}
            type="password"
            autoComplete="off"
            forwardedRef={inputRef}
            required
            data-lpignore
        />
    );
};
